import React from 'react';
import Navbar from '../../Navbar';
import Sidebar from '../../Sidebar';
import { BlockMath } from 'react-katex';
import '../../../styles/Tutorial3.css';

const ChemicalReactionEngineeringLec3 = () => {
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX:  'hidden',
          minHeight: 0,
        }}>
      <div className="cre3-container" style={{ background: 'var(--t3-bg)', minHeight: '100%' }}>

        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <nav className="cre3-sidebar">
          <div className="cre3-sidebar-header">
            <h1>CRE Course</h1>
            <p>Prof. Fogler · Lecture 3</p>
          </div>

          <div className="cre3-nav-section">Review — Ch 1 &amp; 2</div>
          <a className="cre3-nav-item" href="#mole-balance">Mole Balances (GMBE)</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#mb-conversion">↳ In Terms of Conversion</a>
          <a className="cre3-nav-item" href="#conversion">Conversion &amp; Definition</a>
          <a className="cre3-nav-item" href="#levenspiel">Levenspiel Plots</a>
          <a className="cre3-nav-item" href="#series">Reactors in Series</a>

          <div className="cre3-nav-section">Building Block 2 — Ch 3</div>
          <a className="cre3-nav-item" href="#rate-laws">Rate Laws</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#power-law">↳ Power Law Model</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#elementary">↳ Elementary Rate Laws</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#relative-rates">↳ Relative Rates</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#reversible">↳ Reversible Reactions</a>
          <a className="cre3-nav-item" href="#algorithm">Algorithm: −rA = f(X)</a>

          <div className="cre3-nav-section">Arrhenius &amp; Activation</div>
          <a className="cre3-nav-item" href="#arrhenius">Arrhenius Equation</a>
          <a className="cre3-nav-item" href="#activation-energy">Activation Energy</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#mass-action">↳ Law of Mass Action</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#energy-barriers">↳ Energy Barriers</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#boltzmann">↳ Boltzmann Distribution</a>
          <a className="cre3-nav-item cre3-nav-item--sub" href="#fraction-ea">↳ Fraction with E &gt; EA</a>

          <div className="cre3-nav-section">Reference</div>
          <a className="cre3-nav-item" href="#rate-table">Rate Law Examples</a>
          <a className="cre3-nav-item" href="#symbols">Key Symbols</a>
        </nav>

        {/* ── MAIN CONTENT ────────────────────────────────────────── */}
        <main className="cre3-main">
          <div className="cre3-main-inner">

            {/* HERO */}
            <div className="cre3-hero">
              <div className="cre3-hero-tag">Open Course Notes · Chapter 3</div>
              <h1>CRE Lecture 3 — Rate Laws &amp; Arrhenius</h1>
              <p className="cre3-hero-desc">
                A complete study guide for Lecture 3 of Prof. H. Scott Fogler's Chemical Reaction
                Engineering course — reviewing mole balances, introducing rate laws, the power law
                model, relative reaction rates, and the Arrhenius equation with activation energy concepts.
              </p>
              <div className="cre3-hero-meta">
                <span className="cre3-meta-item"><span className="cre3-meta-dot"></span>Fogler's Elements of CRE, 6th Ed.</span>
                <span className="cre3-meta-item"><span className="cre3-meta-dot"></span>Lecture 3 · Chapter 3</span>
                <span className="cre3-meta-item"><span className="cre3-meta-dot"></span>University of Michigan</span>
              </div>
            </div>

            {/* TOC */}
            <div className="cre3-toc-box">
              <h3>Quick Navigation</h3>
              <div className="cre3-toc-grid">
                <a className="cre3-toc-link" href="#mole-balance">GMBE Mole Balance</a>
                <a className="cre3-toc-link" href="#conversion">Conversion Definition</a>
                <a className="cre3-toc-link" href="#mb-conversion">Mole Balance in X</a>
                <a className="cre3-toc-link" href="#levenspiel">Levenspiel Plots</a>
                <a className="cre3-toc-link" href="#series">Reactors in Series</a>
                <a className="cre3-toc-link" href="#power-law">Power Law Model</a>
                <a className="cre3-toc-link" href="#relative-rates">Relative Rates</a>
                <a className="cre3-toc-link" href="#reversible">Reversible Reactions</a>
                <a className="cre3-toc-link" href="#arrhenius">Arrhenius Equation</a>
                <a className="cre3-toc-link" href="#activation-energy">Activation Energy</a>
                <a className="cre3-toc-link" href="#boltzmann">Boltzmann Distribution</a>
                <a className="cre3-toc-link" href="#fraction-ea">Fraction with E &gt; EA</a>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════
                REVIEW: MOLE BALANCE
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="mole-balance">
              <div className="cre3-lec-badge cre3-lec-badge--lec1">Review · Chapter 1</div>
              <div className="cre3-section-label">Building Block 1</div>
              <h2>Mole Balances — GMBE</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The General Mole Balance Equation (GMBE) applied to the four major reactor types
                for the general reaction <strong>A → B</strong>. Each reactor type has a differential
                form, an algebraic (design) form, and an integral form.
              </p>

              <div className="cre3-callout cre3-callout--key">
                <p><strong>GMBE Principle:</strong> For any system volume, the rate of accumulation of
                species A equals the molar flow in minus the molar flow out plus the rate of generation
                by chemical reaction.</p>
              </div>

              {/* Reactor equations table */}
              <table className="cre3-reactor-table">
                <thead>
                  <tr>
                    <th>Reactor</th>
                    <th>Differential</th>
                    <th>Algebraic</th>
                    <th>Integral</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--batch">Batch</span></td>
                    <td>
                      <BlockMath math="\frac{dN_A}{dt} = r_A V" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="t = \int_{N_{A0}}^{N_A} \frac{dN_A}{r_A V}" />
                    </td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--cstr">CSTR</span></td>
                    <td>—</td>
                    <td>
                      <BlockMath math="V = \frac{F_{A0} - F_A}{-r_A}" />
                    </td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--pfr">PFR</span></td>
                    <td>
                      <BlockMath math="\frac{dF_A}{dV} = r_A" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="V = \int_{F_{A0}}^{F_A} \frac{dF_A}{dr_A}" />
                    </td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--pbr">PBR</span></td>
                    <td>
                      <BlockMath math="\frac{dF_A}{dW} = r_A'" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="W = \int_{F_{A0}}^{F_A} \frac{dF_A}{r_A'}" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* ═══════════════════════════════════════════════════════
                REVIEW: CONVERSION DEFINITION
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="conversion">
              <div className="cre3-lec-badge cre3-lec-badge--lec1">Review · Chapter 2</div>
              <div className="cre3-section-label">Conversion</div>
              <h2>Conversion — Definition &amp; Stoichiometry</h2>
              <div className="cre3-section-divider"></div>
              <p>
                We choose the <strong>limiting reactant A</strong> as the basis of calculation for
                conversion. The general reaction is normalised by dividing through by the stoichiometric
                coefficient of A:
              </p>
              <div className="cre3-two-col">
                <div className="cre3-eq-box cre3-eq-box--blue">
                  <div className="cre3-eq-label">General Reaction</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="aA + bB \longrightarrow cC + dD" />
                    <BlockMath math="A + \frac{b}{a}B \longrightarrow \frac{c}{a}C + \frac{d}{a}D" />
                  </div>
                </div>
                <div className="cre3-eq-box cre3-eq-box--green">
                  <div className="cre3-eq-label">Conversion X</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="X = \frac{\text{moles of A reacted}}{\text{moles of A fed}}" />
                  </div>
                  <div className="cre3-eq-note">X ranges from 0 (no reaction) to 1 (complete reaction of A).</div>
                </div>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                MOLE BALANCE IN TERMS OF X
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="mb-conversion">
              <div className="cre3-lec-badge cre3-lec-badge--lec1">Review · Chapter 2</div>
              <div className="cre3-section-label">Building Block 1</div>
              <h2>Mole Balance in Terms of Conversion</h2>
              <div className="cre3-section-divider"></div>
              <p>
                Expressing the mole balances in terms of conversion X instead of molar flow rates makes
                reactor design equations much easier to integrate and solve analytically or numerically.
              </p>

              <table className="cre3-reactor-table">
                <thead>
                  <tr>
                    <th>Reactor</th>
                    <th>Differential</th>
                    <th>Algebraic</th>
                    <th>Integral</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--batch">Batch</span></td>
                    <td>
                      <BlockMath math="N_{A0}\frac{dX}{dt} = -r_A V" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="t = N_{A0}\int_0^X \frac{dX}{-r_A V}" />
                    </td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--cstr">CSTR</span></td>
                    <td>—</td>
                    <td>
                      <BlockMath math="V = \frac{F_{A0}\,X}{-r_A}" />
                    </td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--pfr">PFR</span></td>
                    <td>
                      <BlockMath math="F_{A0}\frac{dX}{dV} = -r_A" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="V = F_{A0}\int_0^X \frac{dX}{-r_A}" />
                    </td>
                  </tr>
                  <tr>
                    <td><span className="cre3-reactor-chip cre3-chip--pbr">PBR</span></td>
                    <td>
                      <BlockMath math="F_{A0}\frac{dX}{dW} = -r_A'" />
                    </td>
                    <td>—</td>
                    <td>
                      <BlockMath math="W = F_{A0}\int_0^X \frac{dX}{-r_A'}" />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="cre3-callout cre3-callout--warn">
                <p><strong>Note on Graphs:</strong> For a Batch reactor, X rises with time t. For a CSTR,
                the exit conversion is set by the algebraic design equation (no integral needed). For PFR
                and PBR, X rises monotonically along V or W respectively.</p>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                LEVENSPIEL PLOTS
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="levenspiel">
              <div className="cre3-lec-badge cre3-lec-badge--lec1">Review · Chapter 2</div>
              <div className="cre3-section-label">Graphical Design</div>
              <h2>Levenspiel Plots</h2>
              <div className="cre3-section-divider"></div>
              <p>
                A Levenspiel plot is a graph of <strong>F<sub>A0</sub> / (−r<sub>A</sub>)</strong> versus
                conversion X. It provides a powerful graphical method to determine reactor volumes:
              </p>
              <div className="cre3-two-col">
                <div className="cre3-eq-box cre3-eq-box--blue">
                  <div className="cre3-eq-label">CSTR Volume</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="V_{\text{CSTR}} = \frac{F_{A0}}{-r_A}\bigg|_{X_{\text{exit}}} \cdot X_{\text{exit}}" />
                  </div>
                  <div className="cre3-eq-note">
                    CSTR volume = <strong>rectangle</strong> under the Levenspiel curve
                    at the exit conversion. Since CSTR operates at exit conditions, it uses the
                    highest value of F<sub>A0</sub>/(−r<sub>A</sub>) along the path.
                  </div>
                </div>
                <div className="cre3-eq-box cre3-eq-box--green">
                  <div className="cre3-eq-label">PFR Volume</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="V_{\text{PFR}} = F_{A0}\int_0^{X_{\text{exit}}} \frac{dX}{-r_A}" />
                  </div>
                  <div className="cre3-eq-note">
                    PFR volume = <strong>area under</strong> the Levenspiel curve from 0 to X<sub>exit</sub>.
                    For a normal (monotonically increasing) Levenspiel curve, PFR volume &lt; CSTR volume.
                  </div>
                </div>
              </div>
              <div className="cre3-callout cre3-callout--tip">
                <p><strong>Key Insight:</strong> For a rate that decreases with conversion (typical),
                V<sub>PFR</sub> &lt; V<sub>CSTR</sub> for the same conversion. When the Levenspiel plot
                has a minimum (autocatalytic reactions), a CSTR followed by a PFR may be optimal.</p>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                REACTORS IN SERIES
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="series">
              <div className="cre3-lec-badge cre3-lec-badge--lec1">Review · Chapter 2</div>
              <div className="cre3-section-label">Series Configurations</div>
              <h2>Reactors in Series</h2>
              <div className="cre3-section-divider"></div>
              <p>
                When reactors are arranged in series (with no side streams), a single overall conversion
                can be defined based on the total moles of A fed to the <em>first</em> reactor:
              </p>
              <div className="cre3-eq-box">
                <div className="cre3-eq-label">Cumulative Conversion at Point i</div>
                <div className="cre3-eq-main">
                  <BlockMath math="X_i = \frac{\text{moles of A reacted up to point } i}{\text{moles of A fed to first reactor}}" />
                </div>
                <div className="cre3-eq-note">
                  <strong>Valid only when there are no side streams.</strong> For a CSTR–PFR–CSTR series:
                  X<sub>1</sub> exits the first CSTR, X<sub>2</sub> exits the PFR, and X<sub>3</sub> exits the
                  second CSTR — all referenced to the original feed.
                </div>
              </div>

              <h3>CSTR Volume in a Series</h3>
              <div className="cre3-eq-box cre3-eq-box--blue">
                <div className="cre3-eq-label">CSTR i Design Equation</div>
                <div className="cre3-eq-main">
                  <BlockMath math="V_i = \frac{F_{A0}(X_i - X_{i-1})}{-r_{Ai}}" />
                </div>
                <div className="cre3-eq-note">
                  −r<sub>Ai</sub> is evaluated at exit conditions of reactor i (i.e. at X<sub>i</sub>).
                  On a Levenspiel plot, each CSTR volume corresponds to a rectangle of width (X<sub>i</sub> − X<sub>i−1</sub>)
                  and height F<sub>A0</sub>/(−r<sub>Ai</sub>).
                </div>
              </div>

              <h3>PFR Volume in a Series</h3>
              <div className="cre3-eq-box cre3-eq-box--green">
                <div className="cre3-eq-label">PFR Segment Design Equation</div>
                <div className="cre3-eq-main">
                  <BlockMath math="V_{\text{PFR}} = F_{A0}\int_{X_{i-1}}^{X_i} \frac{dX}{-r_A}" />
                </div>
                <div className="cre3-eq-note">
                  PFR volume in the series is the area under the Levenspiel curve between the inlet
                  and exit conversions of that PFR segment.
                </div>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                RATE LAWS
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="rate-laws">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">Building Block 2</div>
              <h2>Rate Laws</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The <strong>rate law</strong> is a mathematical expression that relates the rate of
                reaction to concentration and temperature. Rate laws are <em>always determined from
                experiment</em> — they cannot be derived from stoichiometry alone (except for elementary
                reactions).
              </p>
              <div className="cre3-callout cre3-callout--key">
                <p><strong>Rate of Disappearance of A</strong> — for many irreversible reactions it can
                be written as:</p>
              </div>
              <div className="cre3-eq-box">
                <div className="cre3-eq-label">General Rate Expression</div>
                <div className="cre3-eq-main">
                  <BlockMath math="-r_A = \left[k_A(T)\right]\left[f_n(C_A, C_B, \ldots)\right]" />
                </div>
                <div className="cre3-eq-note">
                  The rate depends on temperature through k<sub>A</sub>(T) and on concentration through
                  the functional form f<sub>n</sub>. These two dependencies are multiplicatively separable
                  for most reactions.
                </div>
              </div>
            </section>

            {/* POWER LAW */}
            <section className="cre3-section" id="power-law">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">Rate Laws</div>
              <h2>Power Law Model</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The most common empirical rate expression is the <strong>power law model</strong>, where
                the rate is expressed as a product of concentrations raised to powers:
              </p>

              <div className="cre3-eq-box cre3-eq-box--purple">
                <div className="cre3-eq-label">Power Law Rate Expression</div>
                <div className="cre3-eq-main">
                  <BlockMath math="-r_A = k\,C_A^{\,\alpha}\,C_B^{\,\beta}" />
                </div>
                <div className="cre3-eq-note">
                  α = order in A &nbsp;|&nbsp; β = order in B &nbsp;|&nbsp;
                  Overall reaction order = α + β
                </div>
              </div>

              <div className="cre3-card-grid">
                <div className="cre3-card">
                  <div className="cre3-card-icon">α</div>
                  <h4>Order in A</h4>
                  <p>Exponent on C<sub>A</sub>. If α = 1 → first order in A; α = 2 → second order in A. Can be fractional (e.g. 3/2).</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">β</div>
                  <h4>Order in B</h4>
                  <p>Exponent on C<sub>B</sub>. β = 0 means zero order in B (rate independent of B concentration).</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">n</div>
                  <h4>Overall Order</h4>
                  <p>n = α + β. Determines the units of the rate constant k, which depend on the overall reaction order.</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">k</div>
                  <h4>Rate Constant</h4>
                  <p>Temperature-dependent via the Arrhenius equation. Its units adjust so that −r<sub>A</sub> always has units of mol / (dm³·s).</p>
                </div>
              </div>
            </section>

            {/* ELEMENTARY RATE LAWS */}
            <section className="cre3-section" id="elementary">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">Rate Laws</div>
              <h2>Elementary vs. Non-Elementary Rate Laws</h2>
              <div className="cre3-section-divider"></div>
              <p>
                A reaction follows an <strong>elementary rate law</strong> if the reaction orders happen
                to equal the stoichiometric coefficients for the reaction as written. This occurs when
                the mechanism is a single elementary step at the molecular level.
              </p>

              <div className="cre3-example-box">
                <div className="cre3-example-tag">Example — Elementary Rate Law</div>
                <h4>Reaction: 2A + B → 3C</h4>
                <p>If this reaction follows an elementary rate law, the orders match the stoichiometry:</p>
                <div className="cre3-eq-box cre3-eq-box--blue">
                  <div className="cre3-eq-label">Elementary Rate Law for 2A + B → 3C</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="-r_A = k_A\,C_A^2\,C_B" />
                  </div>
                  <div className="cre3-eq-note">
                    2nd order in A, 1st order in B, overall 3rd order.
                    The stoichiometric coefficient of A is 2, so the order in A is 2.
                  </div>
                </div>
              </div>

              <div className="cre3-example-box">
                <div className="cre3-example-tag">Example — Non-Elementary Rate Law</div>
                <h4>Same reaction 2A + B → 3C, but non-elementary</h4>
                <p>The rate could instead be second order in A, zero order in B (from experiment):</p>
                <div className="cre3-eq-box cre3-eq-box--purple">
                  <div className="cre3-eq-label">Non-Elementary Rate Law</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="-r_A = k_A\,C_A^2" />
                    <BlockMath math="-r_B = k_B\,C_A^2" />
                    <BlockMath math="r_C = k_C\,C_A^2" />
                  </div>
                  <div className="cre3-eq-note">
                    Rate laws are found from experiments, not from stoichiometry. The relationship
                    between k<sub>A</sub>, k<sub>B</sub>, and k<sub>C</sub> is set by stoichiometry (see Relative Rates below).
                  </div>
                </div>
              </div>
            </section>

            {/* RELATIVE RATES */}
            <section className="cre3-section" id="relative-rates">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">Rate Laws</div>
              <h2>Relative Rates of Reaction</h2>
              <div className="cre3-section-divider"></div>
              <p>
                For the general reaction aA + bB → cC + dD, the rates of disappearance/appearance of
                all species are related through the stoichiometric coefficients:
              </p>

              <div className="cre3-eq-box cre3-eq-box--blue">
                <div className="cre3-eq-label">Relative Rate Relationship</div>
                <div className="cre3-eq-main">
                  <BlockMath math="\frac{r_A}{-a} = \frac{r_B}{-b} = \frac{r_C}{c} = \frac{r_D}{d}" />
                </div>
                <div className="cre3-eq-note">
                  Reactants have negative rates (disappear), products have positive rates (appear).
                  This relation holds regardless of whether the rate law is elementary or not.
                </div>
              </div>

              <div className="cre3-example-box">
                <div className="cre3-example-tag">Worked Example · Relative Rates</div>
                <h4>Reaction: 2A + B → 3C, given −r<sub>A</sub> = 10 mol/(dm³·s)</h4>
                <p>Using the stoichiometric ratios r<sub>A</sub>/−2 = r<sub>B</sub>/−1 = r<sub>C</sub>/3:</p>
                <div className="cre3-eq-box cre3-eq-box--green">
                  <div className="cre3-eq-label">Finding −r<sub>B</sub> and r<sub>C</sub></div>
                  <div className="cre3-eq-main">
                    <BlockMath math="-r_B = \frac{-r_A}{2} = \frac{10}{2} = 5 \; \frac{\text{mol}}{\text{dm}^3 \cdot \text{s}}" />
                    <BlockMath math="r_C = \frac{3}{2}(-r_A) = \frac{3}{2}(10) = 15 \; \frac{\text{mol}}{\text{dm}^3 \cdot \text{s}}" />
                  </div>
                </div>
              </div>
            </section>

            {/* REVERSIBLE ELEMENTARY REACTION */}
            <section className="cre3-section" id="reversible">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">Rate Laws</div>
              <h2>Reversible Elementary Reactions</h2>
              <div className="cre3-section-divider"></div>
              <p>
                For a reversible elementary reaction, the net rate is the forward rate minus the
                reverse rate. At equilibrium the net rate is zero, which defines the equilibrium
                constant K<sub>e</sub>.
              </p>

              <div className="cre3-eq-box cre3-eq-box--purple">
                <div className="cre3-eq-label">Reversible Reaction: A + 2B ⇌ 3C</div>
                <div className="cre3-eq-main">
                  <BlockMath math="-r_A = k_A C_A^{\phantom{2}} C_B^2 - k_{-A} C_C^3" />
                  <BlockMath math="-r_A = k_A\!\left[C_A C_B^2 - \frac{C_C^3}{K_e}\right]" />
                </div>
                <div className="cre3-eq-note">
                  where K<sub>e</sub> = k<sub>A</sub> / k<sub>−A</sub> is the equilibrium constant.
                  The reaction is 1st order in A, 2nd order in B, overall 3rd order (forward).
                </div>
              </div>

              <h3>Units of the Rate Constant k</h3>
              <p>
                The units of k must be chosen so that −r<sub>A</sub> always has units of
                mol/(dm³·s). For a 3rd-order reaction (1st in A, 2nd in B):
              </p>
              <div className="cre3-eq-box">
                <div className="cre3-eq-label">Units of k for 3rd-Order Rate Law</div>
                <div className="cre3-eq-main">
                  <BlockMath math="[k] = \left[\frac{-r_A}{C_A C_B^2}\right] = \frac{\text{mol}/\text{dm}^3/\text{s}}{(\text{mol}/\text{dm}^3)(\text{mol}/\text{dm}^3)^2} = \frac{\text{dm}^6}{\text{mol}^2 \cdot \text{s}}" />
                </div>
                <div className="cre3-eq-note">
                  General rule: for an n-th order reaction, [k] = dm<sup>3(n−1)</sup> · mol<sup>1−n</sup> · s<sup>−1</sup>
                </div>
              </div>
            </section>

            {/* ALGORITHM */}
            <section className="cre3-section" id="algorithm">
              <div className="cre3-lec-badge cre3-lec-badge--rate">Chapter 3</div>
              <div className="cre3-section-label">CRE Methodology</div>
              <h2>Algorithm: How to Find −r<sub>A</sub> = f(X)</h2>
              <div className="cre3-section-divider"></div>
              <p>
                To use the mole balance design equations we need −r<sub>A</sub> as a function of conversion X
                alone (not concentrations). The algorithm has three steps:
              </p>
              <div className="cre3-steps">
                <div className="cre3-step">
                  <div className="cre3-step-num">1</div>
                  <div className="cre3-step-content">
                    <h4>Rate Law — express rate in terms of concentrations</h4>
                    <div className="cre3-eq-main">
                      <BlockMath math="-r_A = g(C_i)" />
                    </div>
                    <p>Start from the experimentally determined power law (or Langmuir–Hinshelwood) expression.</p>
                  </div>
                </div>
                <div className="cre3-step">
                  <div className="cre3-step-num">2</div>
                  <div className="cre3-step-content">
                    <h4>Stoichiometry — express concentrations in terms of X</h4>
                    <div className="cre3-eq-main">
                      <BlockMath math="C_i = h(X)" />
                    </div>
                    <p>
                      Liquid phase: C<sub>A</sub> = C<sub>A0</sub>(1 − X).&nbsp;
                      Gas phase: C<sub>A</sub> = C<sub>A0</sub>(1 − X)/(1 + εX) · (P/P<sub>0</sub>)(T<sub>0</sub>/T).
                    </p>
                  </div>
                </div>
                <div className="cre3-step">
                  <div className="cre3-step-num">3</div>
                  <div className="cre3-step-content">
                    <h4>Combine — substitute to get rate as a function of X only</h4>
                    <div className="cre3-eq-main">
                      <BlockMath math="-r_A = f(X)" />
                    </div>
                    <p>
                      Now plug into the mole balance design equation (CSTR, PFR, Batch, PBR) to find
                      reactor volume, space time, or catalyst weight.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                ARRHENIUS EQUATION
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="arrhenius">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Temperature Dependence</div>
              <h2>The Arrhenius Equation</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The specific reaction rate constant k is <em>not</em> truly constant — it depends strongly
                on temperature. This temperature dependence is given by the <strong>Arrhenius Equation</strong>,
                one of the most important relationships in chemical engineering:
              </p>

              <div className="cre3-arrh-card">
                <div className="cre3-eq-main">
                  <BlockMath math="k = A\,e^{-E/RT}" />
                </div>
                <p>The rate constant k increases exponentially with temperature T.</p>
              </div>

              <h3>Parameters of the Arrhenius Equation</h3>
              <div className="cre3-param-list">
                <div className="cre3-param">
                  <div className="cre3-param-sym">E</div>
                  <div className="cre3-param-name">Activation Energy</div>
                  <div className="cre3-param-unit">cal/mol or kJ/mol</div>
                </div>
                <div className="cre3-param">
                  <div className="cre3-param-sym">R</div>
                  <div className="cre3-param-name">Gas Constant</div>
                  <div className="cre3-param-unit">1.987 cal/(mol·K)</div>
                </div>
                <div className="cre3-param">
                  <div className="cre3-param-sym">T</div>
                  <div className="cre3-param-name">Absolute Temperature</div>
                  <div className="cre3-param-unit">Kelvin (K)</div>
                </div>
                <div className="cre3-param">
                  <div className="cre3-param-sym">A</div>
                  <div className="cre3-param-name">Frequency Factor</div>
                  <div className="cre3-param-unit">Same units as k; A ≈ 10¹³</div>
                </div>
              </div>

              <h3>Limiting Behaviour</h3>
              <div className="cre3-two-col">
                <div className="cre3-eq-box cre3-eq-box--blue">
                  <div className="cre3-eq-label">As T → ∞</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="T \to \infty \implies k \to A" />
                  </div>
                  <div className="cre3-eq-note">The frequency factor A is the maximum possible value of k.</div>
                </div>
                <div className="cre3-eq-box cre3-eq-box--red">
                  <div className="cre3-eq-label">As T → 0</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="T \to 0 \implies k \to 0" />
                  </div>
                  <div className="cre3-eq-note">At absolute zero there is no thermal energy to overcome the activation barrier.</div>
                </div>
              </div>

              <h3>Linearised Form — Semilog Plot</h3>
              <p>
                Taking the natural logarithm of the Arrhenius equation gives a linear relationship
                between ln k and 1/T, used to determine E and A from experimental data:
              </p>
              <div className="cre3-eq-box">
                <div className="cre3-eq-label">Linearised Arrhenius Equation</div>
                <div className="cre3-eq-main">
                  <BlockMath math="\ln k_A = \ln A - \frac{E}{R}\left(\frac{1}{T}\right)" />
                </div>
                <div className="cre3-eq-note">
                  A plot of ln k vs. 1/T is a straight line with slope = −E/R and y-intercept = ln A.
                  A steeper slope (more negative) means higher activation energy → stronger temperature sensitivity.
                </div>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                ACTIVATION ENERGY — CONCEPTS
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="activation-energy">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Activation Energy</div>
              <h2>Why is There an Activation Energy?</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The activation energy E<sub>A</sub> represents an energy barrier that reacting molecules
                must overcome for a reaction to occur. There are two physical reasons:
              </p>
              <div className="cre3-card-grid">
                <div className="cre3-card">
                  <div className="cre3-card-icon">🔗</div>
                  <h4>Bond Distortion</h4>
                  <p>Molecules must have enough energy to distort or stretch their bonds in order to break
                  them, enabling the formation of new product bonds.</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">⚡</div>
                  <h4>Steric &amp; Electron Repulsion</h4>
                  <p>As reacting molecules come together they must overcome steric hindrance and
                  electron-cloud repulsion forces before bonding can take place.</p>
                </div>
              </div>
            </section>

            {/* LAW OF MASS ACTION */}
            <section className="cre3-section" id="mass-action">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Activation Energy · Concept 1</div>
              <h2>Law of Mass Action</h2>
              <div className="cre3-section-divider"></div>
              <p>
                The rate of reaction increases with increasing concentration of reactants owing to the
                corresponding increase in the number of molecular collisions. For many irreversible reactions:
              </p>
              <div className="cre3-eq-box cre3-eq-box--blue">
                <div className="cre3-eq-label">Rate = Rate Constant × Function of Concentrations</div>
                <div className="cre3-eq-main">
                  <BlockMath math="-r_A = \bigl[k_A(T)\bigr]\bigl[f_n(C_A, C_B, \ldots)\bigr]" />
                </div>
                <div className="cre3-eq-note">
                  Temperature and concentration effects are separable. For the elementary reaction
                  AB + C ⇌ A + BC:
                </div>
              </div>
              <div className="cre3-eq-box cre3-eq-box--green">
                <div className="cre3-eq-label">Elementary Reaction Example with Arrhenius Rate Constant</div>
                <div className="cre3-eq-main">
                  <BlockMath math="-r_{AB} = k_{AB}\,C_{AB}\,C_C = A_{AB}\,e^{-E/RT}\,C_{AB}\,C_C" />
                </div>
                <div className="cre3-eq-note">E = Activation Energy (kJ/mol)</div>
              </div>
            </section>

            {/* ENERGY BARRIERS */}
            <section className="cre3-section" id="energy-barriers">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Activation Energy · Concept 2</div>
              <h2>Potential Energy Surfaces &amp; Energy Barriers</h2>
              <div className="cre3-section-divider"></div>
              <p>
                As two reacting molecules (AB and C) approach each other, the potential energy of the
                system rises due to molecular repulsion. The system must pass over an energy barrier
                (the transition state) to form products:
              </p>
              <div className="cre3-eq-box cre3-eq-box--purple">
                <div className="cre3-eq-label">Reaction Pathway with Transition State</div>
                <div className="cre3-eq-main">
                  <BlockMath math="AB + C \;\rightleftharpoons\; A{\cdot}B{\cdot}C \;\rightleftharpoons\; A + BC" />
                </div>
                <div className="cre3-eq-note">
                  A·B·C is the activated complex (transition state). The energy required to reach
                  this state from the reactants is the activation energy E<sub>A</sub> (or E<sub>B</sub>, the energy barrier).
                </div>
              </div>

              <h3>Reaction Coordinate Diagram</h3>
              <p>
                The reaction coordinate is a measure of the progress of the reaction from reactants
                to products. Key energies on the diagram:
              </p>
              <div className="cre3-card-grid">
                <div className="cre3-card">
                  <div className="cre3-card-icon">📈</div>
                  <h4>Forward Activation Energy (E<sub>A</sub>)</h4>
                  <p>Energy required to go from reactants (A + BC) up to the transition state A·B·C.
                  This is E in the Arrhenius equation.</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">📉</div>
                  <h4>Reverse Activation Energy</h4>
                  <p>Energy required to go from products (AB + C) up to the transition state.
                  Larger for exothermic reactions, smaller for endothermic reactions.</p>
                </div>
                <div className="cre3-card">
                  <div className="cre3-card-icon">ΔH</div>
                  <h4>Heat of Reaction ΔH<sub>Rx</sub></h4>
                  <p>Difference between product energy and reactant energy. Negative for exothermic
                  (energy released), positive for endothermic (energy absorbed).</p>
                </div>
              </div>
            </section>

            {/* BOLTZMANN DISTRIBUTION */}
            <section className="cre3-section" id="boltzmann">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Activation Energy · Concept 3</div>
              <h2>Boltzmann Distribution of Molecular Velocities</h2>
              <div className="cre3-section-divider"></div>
              <p>
                Not all molecules have the same kinetic energy — they follow the
                <strong> Maxwell–Boltzmann distribution</strong>. Only the fraction of molecules
                with energy exceeding E<sub>A</sub> can react. The distribution of relative velocities is:
              </p>
              <div className="cre3-eq-box cre3-eq-box--blue">
                <div className="cre3-eq-label">Maxwell–Boltzmann Distribution of Velocities</div>
                <div className="cre3-eq-main">
                  <BlockMath math="f(U,T)\,dU = 4\pi\!\left(\frac{m}{2\pi k_B T}\right)^{\!3/2} e^{-mU^2/2k_BT}\,U^2\,dU" />
                </div>
                <div className="cre3-eq-note">
                  f(U,T) dU = fraction of relative velocities between U and U + dU.
                  k<sub>B</sub> = 3.29 × 10<sup>−24</sup> cal/(molecule·K), m = reduced mass, U = relative velocity.
                </div>
              </div>

              <h3>Distribution in Terms of Energy per Mole</h3>
              <p>
                Substituting E = ½mU² and converting from energy per molecule (e) to energy per mole (E):
              </p>
              <div className="cre3-eq-box cre3-eq-box--green">
                <div className="cre3-eq-label">Energy Distribution (per molecule)</div>
                <div className="cre3-eq-main">
                  <BlockMath math="f(E,T)\,dE = \frac{2\pi}{(2\pi k_B T)^{3/2}}\,E^{1/2}\,e^{-E/k_BT}\,dE" />
                </div>
              </div>
              <div className="cre3-eq-box cre3-eq-box--purple">
                <div className="cre3-eq-label">Energy Distribution (per mole) — Eq. 3-20</div>
                <div className="cre3-eq-main">
                  <BlockMath math="f(E,T) = 2\pi\!\left(\frac{1}{\pi RT}\right)^{\!3/2} E^{1/2}\,\exp\!\left(-\frac{E}{RT}\right)" />
                </div>
                <div className="cre3-eq-note">
                  E in (cal/mol), R in (cal/mol·K), f(E,T) in mol/cal.
                  f(E,T) dE = fraction of collisions with energies between E and E + dE.
                </div>
              </div>

              <h3>Physical Interpretation</h3>
              <p>
                At T<sub>1</sub> = 300 K the distribution is tall and narrow, peaked at low energies.
                At T<sub>2</sub> = 500 K &gt; T<sub>1</sub>, the distribution is shorter and broader —
                more molecules have high energies. The shaded area beyond E<sub>A</sub> represents
                the fraction of collisions that can react.
              </p>

              <div className="cre3-example-box">
                <div className="cre3-example-tag">Numerical Example · Fraction of Collisions</div>
                <h4>Fraction of collisions with energies between 0.25 and 0.35 kcal/mol at T = 300 K</h4>
                <div className="cre3-eq-box cre3-eq-box--blue">
                  <div className="cre3-eq-label">Approximate Fraction (using average value of f)</div>
                  <div className="cre3-eq-main">
                    <BlockMath math="\int_{0.25}^{0.35} f(E,T)\,dE \approx f(0.3,\,300\text{K})\cdot\Delta E = 0.81\,\frac{\text{mol}}{\text{kcal}} \times 0.10\,\frac{\text{kcal}}{\text{mol}} = 0.081" />
                  </div>
                  <div className="cre3-eq-note">8.1% of collisions have energies between 0.25 and 0.35 kcal/mol at 300 K.</div>
                </div>
              </div>
            </section>

            {/* FRACTION WITH E > EA */}
            <section className="cre3-section" id="fraction-ea">
              <div className="cre3-lec-badge cre3-lec-badge--arrh">Chapter 3</div>
              <div className="cre3-section-label">Activation Energy · Concept 3</div>
              <h2>Fraction of Collisions with Energy Greater Than E<sub>A</sub></h2>
              <div className="cre3-section-divider"></div>
              <p>
                The fraction of molecular collisions that have energy equal to or greater than
                the activation energy E<sub>A</sub> is the key quantity that connects molecular
                collision theory to the macroscopic Arrhenius equation:
              </p>
              <div className="cre3-eq-box cre3-eq-box--blue">
                <div className="cre3-eq-label">Cumulative Fraction — Eq. 3-22</div>
                <div className="cre3-eq-main">
                  <BlockMath math="F(E > E_A,\,T) = \int_{E_A}^{\infty} f(E,T)\,dE" />
                </div>
              </div>

              <h3>Analytical Approximation for E<sub>A</sub> &gt; 3RT</h3>
              <div className="cre3-eq-box cre3-eq-box--green">
                <div className="cre3-eq-label">Approximate Fraction — Eq. 3-23</div>
                <div className="cre3-eq-main">
                  <BlockMath math="F(E > E_A,\,T) \approx \frac{2}{\sqrt{\pi}}\left(\frac{E_A}{RT}\right)^{\!1/2} \exp\!\left(-\frac{E_A}{RT}\right)" />
                </div>
                <div className="cre3-eq-note">
                  This expression is proportional to e<sup>−E<sub>A</sub>/RT</sup>, which is precisely
                  the exponential term in the Arrhenius equation — showing why k ∝ e<sup>−E/RT</sup>
                  at the molecular level.
                </div>
              </div>

              <h3>Effect of Temperature — Orders of Magnitude Change</h3>
              <p>
                The exponential sensitivity to temperature is dramatic. For E<sub>A</sub> = 20 kcal/mol:
              </p>
              <div className="cre3-energy-band">
                <div className="cre3-energy-block">
                  <span className="val">1.76 × 10⁻¹⁴</span>
                  <span className="desc">Fraction at T = 300 K</span>
                </div>
                <div className="cre3-energy-block">
                  <span className="val">2.39 × 10⁻⁷</span>
                  <span className="desc">Fraction at T = 600 K</span>
                </div>
                <div className="cre3-energy-block">
                  <span className="val">10⁷×</span>
                  <span className="desc">Difference (7 orders of magnitude!)</span>
                </div>
              </div>

              <div className="cre3-callout cre3-callout--warn">
                <p><strong>Key Takeaway:</strong> Doubling the absolute temperature from 300 K to 600 K
                increases the fraction of reactive collisions by <em>seven orders of magnitude</em> for
                a reaction with E<sub>A</sub> = 20 kcal/mol. This explains why reaction rates are
                exquisitely sensitive to temperature.</p>
              </div>

              <div className="cre3-callout cre3-callout--tip">
                <p><strong>Connection to Arrhenius:</strong> The frequency factor A captures the collision
                frequency and steric factor, while e<sup>−E<sub>A</sub>/RT</sup> is the fraction of
                collisions that have enough energy to react. Together they give k = A·e<sup>−E/RT</sup>.</p>
              </div>
            </section>

            {/* ═══════════════════════════════════════════════════════
                RATE LAW EXAMPLES TABLE
            ═══════════════════════════════════════════════════════ */}
            <section className="cre3-section" id="rate-table">
              <div className="cre3-lec-badge cre3-lec-badge--ref">Reference</div>
              <div className="cre3-section-label">Table 3-1</div>
              <h2>Examples of Reaction Rate Laws</h2>
              <div className="cre3-section-divider"></div>

              <h3>A. First-Order Rate Laws</h3>
              <table className="cre3-table">
                <thead>
                  <tr><th>#</th><th>Reaction</th><th>Rate Law</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>C₂H₆ → C₂H₄ + H₂</td>
                    <td><BlockMath math="-r_A = k\,C_{\text{C}_2\text{H}_6}" /></td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>Benzenediazonium chloride decomposition</td>
                    <td><BlockMath math="-r_A = k\,C_{\phi\text{N-NCl}}" /></td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>Ethylene oxide + H₂O → ethylene glycol</td>
                    <td><BlockMath math="-r_A = k\,C_{\text{CH}_2\text{OCH}_2}" /></td>
                  </tr>
                  <tr>
                    <td>4</td>
                    <td>CH₃COCH₃ → CH₂CO + CH₄</td>
                    <td><BlockMath math="-r_A = k\,C_{\text{CH}_3\text{COCH}_3}" /></td>
                  </tr>
                  <tr>
                    <td>5</td>
                    <td>n-C₄H₁₀ ⇌ i-C₄H₁₀ (reversible)</td>
                    <td><BlockMath math="-r_n = k\!\left[C_{n\text{C}_4} - \frac{C_{i\text{C}_4}}{K_C}\right]" /></td>
                  </tr>
                </tbody>
              </table>

              <h3>B. Second-Order Rate Laws</h3>
              <table className="cre3-table">
                <thead>
                  <tr><th>#</th><th>Reaction</th><th>Rate Law</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>o-Nitochlorobenzene + 2NH₃ → o-Nitraniline + NH₄Cl</td>
                    <td><BlockMath math="-r_A = k_{\text{ONCB}}\,C_{\text{ONCB}}\,C_{\text{NH}_3}" /></td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>CNBr + CH₃NH₂ → CH₃Br + NCNH₂</td>
                    <td><BlockMath math="-r_A = k\,C_{\text{CNBr}}\,C_{\text{CH}_3\text{NH}_2}" /></td>
                  </tr>
                  <tr>
                    <td>3</td>
                    <td>Esterification: A + B ⇌ C + D</td>
                    <td><BlockMath math="-r_A = k\!\left[C_A C_B - \frac{C_C C_D}{K_C}\right]" /></td>
                  </tr>
                </tbody>
              </table>

              <h3>C. Non-Elementary Rate Laws</h3>
              <table className="cre3-table">
                <thead>
                  <tr><th>Type</th><th>Reaction</th><th>Rate Law</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Homogeneous</td>
                    <td>CH₃CHO → CH₄ + CO</td>
                    <td><BlockMath math="-r_{\text{CH}_3\text{CHO}} = k\,C_{\text{CH}_3\text{CHO}}^{3/2}" /></td>
                  </tr>
                  <tr>
                    <td>Heterogeneous</td>
                    <td>Cumene → Benzene + Propylene</td>
                    <td><BlockMath math="-r_C = \frac{k(P_C - P_B P_P / K_P)}{1 + K_B P_B + K_C P_C}" /></td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* KEY SYMBOLS */}
            <section className="cre3-section" id="symbols">
              <div className="cre3-lec-badge cre3-lec-badge--ref">Reference</div>
              <div className="cre3-section-label">Reference</div>
              <h2>Key Symbols &amp; Variables — Lecture 3</h2>
              <div className="cre3-section-divider"></div>
              <table className="cre3-table">
                <thead>
                  <tr><th>Symbol</th><th>Meaning</th><th>Typical Units</th></tr>
                </thead>
                <tbody>
                  <tr><td>X</td><td>Conversion of limiting reactant A</td><td>dimensionless (0 to 1)</td></tr>
                  <tr><td>−r<sub>A</sub></td><td>Rate of disappearance of A per unit volume</td><td>mol/(dm³·s)</td></tr>
                  <tr><td>−r'<sub>A</sub></td><td>Rate of disappearance per unit catalyst mass (PBR)</td><td>mol/(kg·s)</td></tr>
                  <tr><td>k</td><td>Specific reaction rate constant (Arrhenius-dependent)</td><td>varies with reaction order</td></tr>
                  <tr><td>A</td><td>Frequency (pre-exponential) factor</td><td>same as k; ≈ 10¹³</td></tr>
                  <tr><td>E</td><td>Activation energy</td><td>cal/mol or kJ/mol</td></tr>
                  <tr><td>R</td><td>Universal gas constant</td><td>1.987 cal/(mol·K) or 8.314 J/(mol·K)</td></tr>
                  <tr><td>T</td><td>Absolute temperature</td><td>K</td></tr>
                  <tr><td>K<sub>e</sub></td><td>Equilibrium constant (= k<sub>fwd</sub>/k<sub>rev</sub>)</td><td>varies</td></tr>
                  <tr><td>α, β</td><td>Reaction orders in species A, B (power law)</td><td>dimensionless</td></tr>
                  <tr><td>C<sub>A</sub></td><td>Concentration of species A</td><td>mol/dm³</td></tr>
                  <tr><td>F<sub>A0</sub></td><td>Molar feed rate of A</td><td>mol/s</td></tr>
                  <tr><td>k<sub>B</sub></td><td>Boltzmann's constant</td><td>3.29 × 10⁻²⁴ cal/(molecule·K)</td></tr>
                  <tr><td>f(E,T)</td><td>Energy distribution function (Boltzmann)</td><td>mol/cal</td></tr>
                  <tr><td>E<sub>A</sub></td><td>Activation energy threshold for reaction</td><td>kcal/mol</td></tr>
                </tbody>
              </table>
            </section>

            <div className="cre3-footer">
              Based on: H. Scott Fogler, "Elements of Chemical Reaction Engineering," 6th Edition ·
              University of Michigan · Lecture 3, Chapter 3 · Course notes compiled for educational use
            </div>

          </div>
        </main>
      </div>

        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>       
  );
};

export default ChemicalReactionEngineeringLec3;